// app/components/Plugins/NostrRelay/NostrRelay.tsx:
'use client'

import React, { useEffect, useState } from 'react'
// Use the "pure" API to generate keys, sign and verify events.
import {
  generateSecretKey,
  getPublicKey,
  finalizeEvent,
  verifyEvent
} from 'nostr-tools/pure'
// Use the Relay class to interact with a relay.
import { Relay } from 'nostr-tools/relay'
/**
 * @uiComponent NostrRelay - Decentralized Social Networking Plugin
 * 
 * A React component that provides decentralized social networking capabilities using the Nostr protocol.
 * Enables users to publish posts, receive real-time updates, and interact with a distributed network
 * of relays without centralized control. Supports rich media content including images and videos.
 * 
 * @uiWorkflow "Connect and Publish Posts to Nostr Network"
 * 1. Component automatically connects to the configured Nostr relay on mount
 * 2. User can view their generated public key (demo mode with auto-generated keypair)
 * 3. User types their message in the textarea input field
 * 4. User clicks "Publish Post" to broadcast their message to the Nostr network
 * 5. Post is cryptographically signed and published to the relay
 * 6. Success confirmation is logged and input field is cleared
 * 
 * @uiWorkflow "Receive and Display Real-time Posts"
 * 1. Component subscribes to kind 1 events (text posts) from the relay
 * 2. Real-time posts from other users appear in the "Recent Posts" section
 * 3. Each post displays timestamp, content, and truncated public key of author
 * 4. Media URLs (images/videos) are automatically detected and rendered inline
 * 5. Non-media URLs are converted to clickable links
 * 6. Posts are displayed in reverse chronological order (newest first)
 * 
 * @uiWorkflow "View Rich Media Content in Posts"
 * 1. System automatically scans post content for media URLs
 * 2. Images (PNG, JPG, GIF, WebP, BMP) are displayed as inline images
 * 3. Videos (MP4, WebM, OGG) are rendered with native video controls
 * 4. Media content is displayed below the text content of each post
 * 5. Multiple media items in a single post are displayed sequentially
 * 6. Non-media URLs remain as clickable text links
 */

/*
  For this demo, we use a public relay.
  You can replace this URL with your preferred relay.
*/
const RELAY_URL = 'wss://relay.damus.io'

// Define a simple interface for Nostr events.
interface NostrEvent {
  id: string
  pubkey: string
  created_at: number
  kind: number
  tags: string[][]
  content: string
  sig: string
}

/* 
  Use a single regex that captures media URLs for images (png, jpg, jpeg, gif, webp, bmp)
  and videos (mp4, webm, ogg). 
*/
const mediaRegex =
  /https?:\/\/\S+\.(png|jpe?g|gif|webp|bmp|mp4|webm|ogg)(\?\S+)?/gi

// Helper: Check if a URL is a video.
const isVideoUrl = (url: string): boolean => {
  return /\.(mp4|webm|ogg)(\?\S+)?$/i.test(url)
}

// Helper: Check if a URL is an image.
const isImageUrl = (url: string): boolean => {
  return /\.(png|jpe?g|gif|webp|bmp)(\?\S+)?$/i.test(url)
}

// Extract all media URLs from text.
const extractMediaUrls = (text: string): string[] => {
  const matches = text.match(mediaRegex)
  return matches ? matches : []
}

// Linkify non-media URLs in the text.
// This function splits text by any URL and replaces any non-media URL with a clickable link.
const linkifyText = (text: string): React.ReactNode[] => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, index) => {
    // If the part exactly matches a URL.
    if (/^https?:\/\/[^\s]+$/.test(part)) {
      // If it is not a media URL (image or video), make it clickable.
      if (!mediaRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#88a1ac' }}
          >
            {part}
          </a>
        )
      }
      // Otherwise, return it as plain text.
      return <span key={index}>{part}</span>
    }
    // Non-URL text is returned as-is.
    return <span key={index}>{part}</span>
  })
}

// Convert Uint8Array to hex string (inline to avoid @noble/hashes subpath resolution with pnpm).
const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

const NostrRelay: React.FC = () => {
  // State to store the relay connection and received posts.
  const [relay, setRelay] = useState<Relay | null>(null)
  const [posts, setPosts] = useState<NostrEvent[]>([])
  const [newPost, setNewPost] = useState<string>('')

  // Generate a key pair (demo mode).
  const [sk] = useState<Uint8Array>(() => generateSecretKey())
  const [pk] = useState<string>(() => getPublicKey(sk))
  const skHex = bytesToHex(sk)

  // Connect to the relay and subscribe to posts (kind: 1).
  useEffect(() => {
    let sub: any
    let activeRelay: Relay | null = null

    const connectRelay = async () => {
      try {
        const r = await Relay.connect(RELAY_URL)
        activeRelay = r
        console.log(`Connected to ${r.url}`)
        // Subscribe to events of kind 1 (simple text posts).
        sub = r.subscribe([{ kinds: [1] }], {
          onevent: (event: NostrEvent) => {
            console.log('Received event:', event)
            setPosts(prev => [event, ...prev])
          },
          oneose: () => {
            sub.close()
          }
        })
        setRelay(r)
      } catch (error) {
        console.error('Error connecting to relay:', error)
      }
    }

    connectRelay()

    // Cleanup on unmount.
    return () => {
      if (activeRelay) {
        activeRelay.close()
      }
    }
  }, [])

  // Publish a new post.
  const publishPost = async () => {
    if (!newPost.trim() || !relay) return

    const eventTemplate = {
      kind: 1,
      created_at: Math.floor(Date.now() / 1000),
      tags: [] as string[][],
      content: newPost
    }

    const signedEvent = finalizeEvent(eventTemplate, sk)

    if (!verifyEvent(signedEvent)) {
      console.error('Event verification failed')
      return
    }

    try {
      await relay.publish(signedEvent)
      console.log('Published event:', signedEvent)
      setNewPost('')
    } catch (error) {
      console.error('Error publishing event:', error)
    }
  }

  return (
    <div
      style={{
        padding: '1rem',
        border: '1px solid #88a1ac49',
        borderRadius: 8,
        margin: '1rem 0',
        // fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontFamily: 'Arial, sans-serif' // Menlo
      }}
    >
      <h3>Nostr Relay Plugin</h3>
      <p>
        <strong>Your Public Key:</strong> {pk.slice(0, 8)}... (demo key)
      </p>
      <p>
        <small>Secret Key (hex): {skHex.slice(0, 8)}...</small>
      </p>

      {/* New post input */}
      <div style={{ marginBottom: '1rem' }}>
        <textarea
          value={newPost}
          onChange={e => setNewPost(e.target.value)}
          placeholder="Write your post here..."
          rows={3}
          style={{ width: '100%', padding: '0.5rem' }}
        />
        <button
          onClick={publishPost}
          style={{ marginTop: '0.5rem', padding: '0.5rem 1rem' }}
        >
          Publish Post
        </button>
      </div>

      {/* List of received posts */}
      <div style={{ overflowY: 'auto' }}>
        <h4>Recent Posts</h4>
        {posts.length === 0 ? (
          <p>No posts yet...</p>
        ) : (
          posts.map((post, idx) => {
            // Extract media URLs (images and videos) from the post content.
            const mediaUrls = extractMediaUrls(post.content)
            // Remove media URLs from text.
            const textWithoutMedia = post.content.replace(mediaRegex, '').trim()

            return (
              <div
                key={idx}
                style={{ padding: '0.5rem', borderBottom: '1px solid #ccc' }}
              >
                <small>
                  {new Date(post.created_at * 1000).toLocaleTimeString()}
                </small>
                {/* Render text with clickable non-media URLs */}
                <p>{linkifyText(textWithoutMedia || post.content)}</p>
                {/* Render media content */}
                {mediaUrls.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {mediaUrls.map((url, index) => {
                      if (isVideoUrl(url)) {
                        return (
                          <video
                            key={index}
                            src={url}
                            controls
                            muted
                            loop
                            style={{ maxWidth: '100%', marginBottom: '0.5rem' }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        )
                      } else {
                        // Treat GIFs and other images as images.
                        return (
                          <img
                            key={index}
                            src={url}
                            alt="media content"
                            style={{
                              maxWidth: '40%',
                              height: 'auto',
                              marginBottom: '0.5rem'
                            }}
                          />
                        )
                      }
                    })}
                  </div>
                )}
                <small style={{ fontSize: '0.8rem', color: '#555' }}>
                  {post.pubkey.slice(0, 8)}...
                </small>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default NostrRelay
